import { useState } from 'react'
import { Button, Fa } from '@eltoro-ui/components'
import { WarningModal } from 'Components/WarningModal'
import { useAppContext } from 'Contexts'
import { trackEvent } from 'Hooks'
import { buildCoreClientUrl } from 'Helpers'

/**
 * EnvironmentToggle — pre-launch mechanism (PLAPP-777).
 *
 * Lets a `coreClientUser` (`eltoro_client_core` role) or a `nextgen_admin` open
 * the equivalent route in the Core Client app, carrying the Keycloak SSO
 * session over (no re-auth). This is a manual, one-shot navigation only — there
 * is no stored preference and no auto-redirect on load, so the two apps never
 * bounce a user back and forth. Mirrors the "View in Gen3" button in the Core
 * Client repo (`eltoro-core-client-platform`); keep the two symmetric. This is a
 * temporary testing mechanism, removed or promoted at the end of the pre-launch
 * period.
 */

// Core Client app destinations (hardcoded — temporary pre-launch mechanism).
const CORE_CLIENT_URL_DEV =
  'https://eltoro-core-client-platform.api.dev.eltoro.com'
const CORE_CLIENT_URL_PROD = 'https://doorstep.eltoro.com'

/**
 * Resolve the Core Client destination for the current environment. Localhost
 * and dev both target the Core Client dev deployment; production targets prod.
 * Mirrors the env logic in `getApiUrl` (Helpers/api-helpers.ts).
 */
const getCoreClientUrl = (): string =>
  process.env.REACT_APP_ENV === 'production' &&
  !/dev/.test(window.location.hostname)
    ? CORE_CLIENT_URL_PROD
    : CORE_CLIENT_URL_DEV

export const EnvironmentToggle = () => {
  const { isCoreClientUser, isAdmin, currentOrg, refreshAuth } = useAppContext()
  const [confirmOpen, setConfirmOpen] = useState(false)

  // The toggle is available to core-client users and to nextgen admins.
  const canUseToggle = Boolean(isCoreClientUser || isAdmin)

  if (!canUseToggle) return null

  const handleSwitch = () => {
    trackEvent('ui_environment_toggled', { direction: 'gen3_to_core' })
    setConfirmOpen(false)
    // Best-effort token refresh so the Core Client can pick up the SSO session;
    // navigate regardless — if the session is stale, Core Client re-auths.
    refreshAuth().finally(() => {
      window.location.href = buildCoreClientUrl(
        getCoreClientUrl(),
        window.location.pathname,
        window.location.search,
        currentOrg?.id,
      )
    })
  }

  return (
    <>
      <Button
        kind="text"
        iconLeft={<Fa icon="right-left" size={1} />}
        onClick={() => setConfirmOpen(true)}
        className="EnvironmentToggle whitespace-nowrap font-medium"
      >
        Switch UI
      </Button>

      {confirmOpen && (
        <WarningModal
          onCancel={() => setConfirmOpen(false)}
          onConfirm={handleSwitch}
          confirm
        >
          <h3 className="text-l font-bold">Switch UI?</h3>
          <p className="mt-2">
            You may have unsaved changes in progress. Anything not saved will be
            lost when you leave for the New UI experience. Continue anyway?
          </p>
        </WarningModal>
      )}
    </>
  )
}
